import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  Clock3,
  ListFilter,
  PauseCircle,
  PhoneCall,
  Settings2,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import {
  SWITCH_DISPLAY_STATUS_LABELS,
  ANALYZE_END_OF_CALL_REPORT_KEY,
  CONTACT_INTELLIGENCE_KEY,
  INCOMING_CALL_TRIAGE_KEY,
  INTELLIGENT_FORM_TRIAGE_KEY,
  type PortalSwitchSummary,
  type SwitchDisplayStatus,
  type SwitchboardResponse,
} from '@command-center/switchboard-sdk';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  cn,
  usePageHeader,
} from '@command-center/ui-system';
import { ApiClientError } from '../lib/api';
import { switchboardApi } from '../lib/switchboard-api';
import { EmptyState, ErrorState, LoadingState } from './parts';
import { SwitchSettingsView } from './SwitchSettingsView';

const QUERY_KEY = ['cascade', 'switchboard'] as const;

export function SwitchboardView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { switchKey } = useParams<{ switchKey: string }>();
  const [actionError, setActionError] = useState<string>();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: switchboardApi.list });
  /*
    Switchboard left the main navigation and is reached from Settings, so its
    name no longer arrives from the nav config — it registers it here, and the
    "Workflow controls" heading that used to restate it below is gone.
  */
  usePageHeader(
    {
      title: 'Switchboard',
      subtitle: 'The automations that work on new Cascade leads.',
    },
    [],
  );
  const toggleMutation = useMutation({
    mutationFn: ({ item, enabled }: { item: PortalSwitchSummary; enabled: boolean }) =>
      switchboardApi.setEnabled(item.definition.key, enabled),
    onSuccess: ({ item }) => {
      queryClient.setQueryData<SwitchboardResponse>(QUERY_KEY, (current) =>
        current
          ? {
              ...current,
              items: current.items.map((value) => (value.id === item.id ? item : value)),
            }
          : current,
      );
      setActionError(undefined);
    },
    onError: (error, { item, enabled }) => {
      setActionError(
        error instanceof Error ? error.message : 'The Switch state could not be changed.',
      );
      if (enabled && error instanceof ApiClientError && error.code === 'switch_setup_required') {
        navigate(`/switchboard/${item.definition.key}`);
      }
    },
  });

  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;
  if (!query.data) return <EmptyState label="No Switchboard data is available." />;

  const selected = switchKey
    ? query.data.items.find((item) => item.definition.key === switchKey)
    : undefined;
  if (switchKey && !selected)
    return <EmptyState label="That Switch is not installed in this portal." />;
  if (selected) {
    return (
      <SwitchSettingsView
        item={selected}
        users={query.data.users}
        canManage={query.data.canManage}
        executionReady={query.data.executionReady}
        onBack={() => navigate('/switchboard')}
        onToggle={(enabled) => changeState(selected, enabled)}
        togglePending={toggleMutation.isPending}
      />
    );
  }

  function changeState(item: PortalSwitchSummary, enabled: boolean) {
    if (!enabled && item.enabled) {
      const confirmed = window.confirm(
        'Pause this Switch? New executions will stop, while settings and history are preserved.',
      );
      if (!confirmed) return;
    }
    toggleMutation.mutate({ item, enabled });
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Badge variant={query.data.executionReady ? 'success' : 'warning'}>
          {query.data.executionReady ? <Zap className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
          {query.data.executionReady ? 'Execution ready' : 'Integration setup in progress'}
        </Badge>
      </div>

      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{actionError}</span>
        </div>
      )}

      {query.data.items.length === 0 ? (
        <EmptyState label="No Switches have been installed for Cascade yet." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {query.data.items.map((item) => (
            <SwitchCard
              key={item.id}
              item={item}
              canManage={query.data.canManage}
              togglePending={toggleMutation.isPending}
              onOpen={() => navigate(`/switchboard/${item.definition.key}`)}
              onToggle={(enabled) => changeState(item, enabled)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SwitchCard({
  item,
  canManage,
  togglePending,
  onOpen,
  onToggle,
}: {
  item: PortalSwitchSummary;
  canManage: boolean;
  togglePending: boolean;
  onOpen: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const status = statusPresentation(item.displayStatus);
  const StatusIcon = status.icon;
  const issue =
    item.displayStatus === 'error' && item.lastErrorMessage
      ? item.lastErrorMessage
      : item.configurationIssues[0]?.message;
  const stateMessage =
    item.displayStatus === 'paused'
      ? `Paused${item.stateChangedBy ? ` by ${item.stateChangedBy}` : ''}${
          item.pausedAt ? ` · ${formatRelative(item.pausedAt)}` : ''
        }`
      : undefined;
  const answerRate = item.usage.estimatedUsage.calls
    ? Math.round((item.usage.answered / item.usage.estimatedUsage.calls) * 100)
    : 0;
  const formTriage = item.definition.key === INTELLIGENT_FORM_TRIAGE_KEY;
  const included = item.definition.key === ANALYZE_END_OF_CALL_REPORT_KEY
    || item.definition.key === INCOMING_CALL_TRIAGE_KEY
    || formTriage;
  const contactIntelligence = item.definition.key === CONTACT_INTELLIGENCE_KEY;
  const triageUsage = item.usage.formTriage;
  const CardIcon = formTriage ? ListFilter : contactIntelligence ? BrainCircuit : PhoneCall;

  return (
    <Card
      className={cn(
        'group flex min-h-[330px] cursor-pointer flex-col transition-shadow hover:shadow-md',
        item.displayStatus === 'error' && 'border-destructive/40',
      )}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpen();
      }}
    >
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <CardIcon className="h-5 w-5" aria-hidden />
          </div>
          <Badge variant={status.variant}>
            <StatusIcon className="h-3 w-3" aria-hidden />
            {SWITCH_DISPLAY_STATUS_LABELS[item.displayStatus]}
          </Badge>
          {included && <Badge variant="muted">Included</Badge>}
        </div>
        <div>
          <CardTitle className="leading-snug">{item.definition.name}</CardTitle>
          <CardDescription className="mt-2 line-clamp-3 leading-relaxed">
            {item.definition.description}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/50 p-3">
          {formTriage ? (
            <>
              <Metric label="Analyzed" value={String(triageUsage?.analyzed ?? 0)} />
              <Metric label="Service requests" value={String(triageUsage?.likelyServiceRequests ?? 0)} />
              <Metric label="Low value" value={String(triageUsage?.lowValueSeparated ?? 0)} />
            </>
          ) : contactIntelligence ? (
            <>
              <Metric label="Updates" value={String(item.usage.summaryUpdates ?? 0)} />
              <Metric label="Executions" value={String(item.usage.executions)} />
              <Metric label="Failed" value={String(item.usage.failed)} />
            </>
          ) : (
            <>
              <Metric label="Calls" value={String(item.usage.estimatedUsage.calls)} />
              <Metric label="Answered" value={String(item.usage.answered)} />
              <Metric label="Qualified" value={String(item.usage.qualified)} />
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {formTriage
            ? (triageUsage?.analyzed ?? 0) > 0
              ? `${triageUsage?.analyzed ?? 0} Form Entr${triageUsage?.analyzed === 1 ? 'y' : 'ies'} analyzed this month`
              : 'No Form Entries analyzed this month'
            : contactIntelligence
            ? (item.usage.summaryUpdates ?? 0) > 0
              ? `${item.usage.summaryUpdates ?? 0} Contact overview update${item.usage.summaryUpdates === 1 ? '' : 's'} this month`
              : 'No recorded summary updates this month'
            : item.usage.estimatedUsage.calls > 0
            ? `${answerRate}% answer rate · ${item.usage.appointmentsScheduled} appointments`
            : 'No recorded usage this month'}
        </p>
        {issue && (
          <div
            className={cn(
              'flex items-start gap-2 rounded-md border p-2.5 text-xs',
              item.displayStatus === 'error'
                ? 'border-destructive/30 bg-destructive/5 text-destructive'
                : 'border-warning/30 bg-warning/5 text-foreground',
            )}
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{issue}</span>
          </div>
        )}
        {!issue && stateMessage && (
          <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/5 p-2.5 text-xs text-foreground">
            <PauseCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{stateMessage}</span>
          </div>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
          <div className="min-w-0 text-xs text-muted-foreground">
            <span className="block font-medium text-foreground">Last activity</span>
            <span>{formatRelative(item.lastActivityAt)}</span>
          </div>
          <div className="flex items-center gap-3" onClick={(event) => event.stopPropagation()}>
            <Button size="sm" variant="ghost" onClick={onOpen}>
              {item.definition.key === ANALYZE_END_OF_CALL_REPORT_KEY || formTriage ? 'View' : 'Manage'} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            {!included && (
              <Switch
                checked={item.enabled}
                disabled={!canManage || togglePending || item.displayStatus === 'coming_soon'}
                aria-label={`${item.enabled ? 'Pause' : 'Enable'} ${item.definition.name}`}
                onCheckedChange={onToggle}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-base font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function statusPresentation(status: SwitchDisplayStatus): {
  icon: LucideIcon;
  variant: 'success' | 'warning' | 'destructive' | 'muted' | 'info';
} {
  switch (status) {
    case 'active':
      return { icon: CheckCircle2, variant: 'success' };
    case 'paused':
      return { icon: PauseCircle, variant: 'warning' };
    case 'setup_required':
      return { icon: Wrench, variant: 'warning' };
    case 'error':
      return { icon: AlertCircle, variant: 'destructive' };
    case 'coming_soon':
      return { icon: Clock3, variant: 'info' };
    default:
      return { icon: Settings2, variant: 'muted' };
  }
}

function formatRelative(value: string | null): string {
  if (!value) return 'No activity yet';
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
